import { HMAC_BYTES, PAYLOAD_BYTES, QR_ID_BYTES } from './constants.js';

/**
 * Codifica un payload a base64url. No usa Buffer nativo de Node para que
 * funcione idéntico en Browser y React Native.
 */
export function encodePayload(parts: { qrIdBytes: Uint8Array; hmacBytes: Uint8Array }): string {
  if (parts.qrIdBytes.length !== QR_ID_BYTES) {
    throw new Error(`qrIdBytes must be ${QR_ID_BYTES} bytes`);
  }
  if (parts.hmacBytes.length !== HMAC_BYTES) {
    throw new Error(`hmacBytes must be ${HMAC_BYTES} bytes`);
  }

  const out = new Uint8Array(PAYLOAD_BYTES);
  out.set(parts.qrIdBytes, 0);
  out.set(parts.hmacBytes, QR_ID_BYTES);

  return uint8ToBase64url(out);
}

export interface DecodedPayload {
  qrIdBytes: Uint8Array;
  qrIdHex: string;
  hmacBytes: Uint8Array;
}

export function decodePayload(payload: string): DecodedPayload {
  const bytes = base64urlToUint8(payload);
  if (bytes.length !== PAYLOAD_BYTES) {
    throw new Error(`Invalid payload length ${bytes.length}, expected ${PAYLOAD_BYTES}`);
  }
  const qrIdBytes = bytes.slice(0, QR_ID_BYTES);
  const hmacBytes = bytes.slice(QR_ID_BYTES);
  return {
    qrIdBytes,
    qrIdHex: bytesToHex(qrIdBytes),
    hmacBytes,
  };
}

// Helpers cross-platform (Browser, Node, RN).

export function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return s;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error('Invalid hex length');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function uint8ToBase64url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i] ?? 0);
  const b64 =
    typeof btoa === 'function'
      ? btoa(bin)
      : globalThis.Buffer
        ? globalThis.Buffer.from(bytes).toString('base64')
        : (() => {
            throw new Error('No base64 encoder available');
          })();
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlToUint8(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  if (typeof atob === 'function') {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  if (globalThis.Buffer) return new Uint8Array(globalThis.Buffer.from(b64, 'base64'));
  throw new Error('No base64 decoder available');
}
